import {
  EnvironmentInjector,
  Injectable,
  runInInjectionContext,
} from '@angular/core';
import {
  collection,
  collectionData,
  collectionGroup,
  deleteDoc,
  doc,
  docData,
  DocumentReference,
  increment,
  orderBy,
  Query,
  query,
  QueryCompositeFilterConstraint,
  QueryConstraint,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import { httpsCallable } from '@angular/fire/functions';
import {
  Entity,
  HoursEntry,
  HoursEntryType,
  SupportOrder,
  SupportPackage,
  SupportTicket,
  TenantConfig,
  TicketEstimation,
  TicketMessage,
  TicketPriority,
  TicketStatus,
} from '@models';
import { convertTimestampsToDates } from '@utils';
import {
  filter,
  firstValueFrom,
  from,
  map,
  Observable,
  switchMap,
} from 'rxjs';
import { SupportSessionService } from './support-session.service';

const PLATFORM_PACKAGES = 'support_packages';
const PLATFORM_ORDERS = 'support_orders';
const PLATFORM_ENTRIES = 'support_entries';

/**
 * Support tickets and hours bags are a single source of truth in the MASTER
 * project. All reads/writes/callables here go through {@link SupportSessionService}:
 *  - role 'master' → the primary (master) project, exactly as before.
 *  - role 'client' → the master project via a scoped support session.
 *
 * The private helpers mirror FirestoreService's semantics (idField 'id',
 * created/updatedAt stamping, soft-delete, timestamp conversion) but bind to the
 * session's Firestore instance instead of the tenant-switching one.
 */
@Injectable({
  providedIn: 'root',
})
export class SupportService {
  constructor(
    private readonly session: SupportSessionService,
    private readonly injector: EnvironmentInjector,
  ) {}

  // ---------------------------------------------------------------------------
  // Master-backed Firestore helpers
  // ---------------------------------------------------------------------------

  private list$<T extends Entity>(
    path: string,
    ...constraints: (QueryConstraint | QueryCompositeFilterConstraint)[]
  ): Observable<T[]> {
    return from(this.session.ensure()).pipe(
      switchMap(() =>
        runInInjectionContext(this.injector, () => {
          const q = query<T, any>(
            collection(this.session.db, path) as unknown as Query<T>,
            ...(constraints as QueryConstraint[]),
          );
          return collectionData<T>(q, { idField: 'id' });
        }),
      ),
      map((rows) =>
        rows
          .filter((d) => (d as { deleted?: boolean }).deleted !== true)
          .map((el) => convertTimestampsToDates(el)),
      ),
    );
  }

  private group$<T extends Entity>(
    groupId: string,
    ...constraints: QueryConstraint[]
  ): Observable<T[]> {
    return from(this.session.ensure()).pipe(
      switchMap(() =>
        runInInjectionContext(this.injector, () => {
          const q = query<T, any>(
            collectionGroup(this.session.db, groupId) as Query<T>,
            ...constraints,
          );
          return collectionData<T>(q, { idField: 'id' });
        }),
      ),
      map((rows) =>
        rows
          .filter((d) => (d as { deleted?: boolean }).deleted !== true)
          .map((el) => convertTimestampsToDates(el)),
      ),
    );
  }

  private doc$<T extends Entity>(path: string, id: string): Observable<T> {
    return from(this.session.ensure()).pipe(
      switchMap(() =>
        runInInjectionContext(this.injector, () => {
          const ref = doc(
            collection(this.session.db, path),
            id,
          ) as DocumentReference<T>;
          return docData<T>(ref, { idField: 'id' });
        }),
      ),
      filter((res): res is T => res !== undefined),
      map((res) => convertTimestampsToDates(res)),
    );
  }

  private genId(path: string): string {
    return doc(collection(this.session.db, path)).id;
  }

  private async createDoc<T>(
    path: string,
    data: Partial<T>,
    id?: string,
  ): Promise<string> {
    await this.session.ensure();
    const docId = id ?? this.genId(path);
    const dbData: any = {
      ...data,
      id: docId,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    await setDoc(doc(collection(this.session.db, path), docId), dbData);
    return docId;
  }

  private async updateDocById<T extends Entity>(
    path: string,
    data: Partial<T> & { id: string },
  ): Promise<void> {
    await this.session.ensure();
    const dbData: any = { ...data, updatedAt: new Date() };
    await updateDoc(doc(collection(this.session.db, path), data.id), dbData);
  }

  /** Soft-delete — mirrors FirestoreService.delete. */
  private softDelete(path: string, id: string): Promise<void> {
    return this.updateDocById(path, { id, deleted: true } as Partial<Entity> & {
      id: string;
    });
  }

  /** Calls a callable on the master project (via the support session). */
  private async callable<Req, Res>(name: string, payload: Req): Promise<Res> {
    await this.session.ensure();
    const fn = httpsCallable<Req, Res>(this.session.functions, name);
    const res = await fn(payload);
    return res.data;
  }

  // ---------------------------------------------------------------------------
  // Tenant billing/support record (master registry)
  // ---------------------------------------------------------------------------

  /**
   * The tenant's billing/support record from the MASTER project — holds
   * `hoursBalance`, plan and status. For standalone clients this is a different
   * document than their local operational tenant config, so the support panel
   * must read the balance from here, not from the local tenant.
   */
  getTenant(tenantId: string): Observable<TenantConfig> {
    return this.doc$<TenantConfig>('tenants', tenantId);
  }

  // ---------------------------------------------------------------------------
  // Support Packages (platform-level — super-admin only)
  // ---------------------------------------------------------------------------

  listPackages(): Observable<SupportPackage[]> {
    return this.list$<SupportPackage>(PLATFORM_PACKAGES);
  }

  createPackage(pkg: Omit<SupportPackage, 'id'>): Promise<string> {
    return this.createDoc<SupportPackage>(PLATFORM_PACKAGES, pkg as SupportPackage);
  }

  updatePackage(id: string, changes: Partial<SupportPackage>): Promise<void> {
    return this.updateDocById<SupportPackage>(PLATFORM_PACKAGES, {
      id,
      ...changes,
    });
  }

  /**
   * Creates or updates a support package and syncs it with Stripe (Product + Price).
   * Calls the `upsertSupportPackage` Cloud Function — super-admin only.
   */
  upsertPackageViaStripe(data: {
    id?: string;
    name: string;
    hours: number;
    price: number;
    active: boolean;
  }): Promise<{ id: string; stripePriceId: string }> {
    return this.callable<typeof data, { id: string; stripePriceId: string }>(
      'upsertSupportPackage',
      data,
    );
  }

  deletePackage(id: string): Promise<void> {
    return this.softDelete(PLATFORM_PACKAGES, id);
  }

  // ---------------------------------------------------------------------------
  // Support Orders
  // ---------------------------------------------------------------------------

  /** All orders — for super-admin list */
  listAllOrders(): Observable<SupportOrder[]> {
    return this.list$<SupportOrder>(
      PLATFORM_ORDERS,
      orderBy('createdAt', 'desc'),
    );
  }

  /** Orders for a specific tenant */
  listOrdersByTenant(tenantId: string): Observable<SupportOrder[]> {
    return this.list$<SupportOrder>(
      PLATFORM_ORDERS,
      where('tenantId', '==', tenantId),
      orderBy('createdAt', 'desc'),
    );
  }

  getOrder(id: string): Observable<SupportOrder> {
    return this.doc$<SupportOrder>(PLATFORM_ORDERS, id);
  }

  createOrder(order: Omit<SupportOrder, 'id'>): Promise<string> {
    return this.createDoc<SupportOrder>(PLATFORM_ORDERS, order as SupportOrder);
  }

  /**
   * Creates a pending transfer order via Cloud Function (server-side).
   * The CF reads the real package data — the client only supplies packageId.
   */
  createTransferOrder(packageId: string): Promise<{ orderId: string }> {
    return this.callable<{ packageId: string }, { orderId: string }>(
      'createTransferOrder',
      { packageId },
    );
  }

  /**
   * Super-admin: confirms a bank transfer payment.
   * Atomically marks the order as paid, creates a credit entry,
   * and increments the tenant hoursBalance.
   */
  async confirmTransferOrder(orderId: string): Promise<void> {
    await this.callable<{ orderId: string }, { success: boolean }>(
      'confirmTransferOrder',
      { orderId },
    );
  }

  /**
   * Creates a Stripe Checkout Session for purchasing a support hours package.
   * Returns the Stripe-hosted payment URL to redirect the user to.
   * Runs on the master project (platform Stripe) via the support session.
   */
  createStripeCheckoutSession(
    packageId: string,
    successUrl: string,
    cancelUrl: string,
  ): Promise<{ url: string; orderId: string }> {
    return this.callable<
      { packageId: string; successUrl: string; cancelUrl: string },
      { url: string; orderId: string }
    >('createSupportCheckoutSession', { packageId, successUrl, cancelUrl });
  }

  confirmOrder(id: string, confirmedByUid: string): Promise<void> {
    return this.updateDocById<SupportOrder>(PLATFORM_ORDERS, {
      id,
      status: 'paid',
      paidAt: new Date(),
      confirmedBy: confirmedByUid,
    });
  }

  cancelOrder(id: string): Promise<void> {
    return this.updateDocById<SupportOrder>(PLATFORM_ORDERS, {
      id,
      status: 'cancelled',
    });
  }

  deleteOrder(id: string): Promise<void> {
    return this.softDelete(PLATFORM_ORDERS, id);
  }

  // ---------------------------------------------------------------------------
  // Hours Entries (ledger)
  // ---------------------------------------------------------------------------

  /** All entries for a tenant (used by tenant panel and super-admin) */
  listEntriesByTenant(tenantId: string): Observable<HoursEntry[]> {
    return this.list$<HoursEntry>(
      PLATFORM_ENTRIES,
      where('tenantId', '==', tenantId),
      orderBy('createdAt', 'desc'),
    );
  }

  /** All entries — for super-admin global view */
  listAllEntries(): Observable<HoursEntry[]> {
    return this.list$<HoursEntry>(
      PLATFORM_ENTRIES,
      orderBy('createdAt', 'desc'),
    );
  }

  /**
   * Add a manual credit or debit entry (super-admin).
   * Also updates the tenant's hoursBalance in the `tenants` collection.
   */
  async addManualEntry(
    tenantId: string,
    type: HoursEntryType,
    hours: number,
    description: string,
    createdBy: string,
    relatedTicketId?: string,
    relatedOrderId?: string,
  ): Promise<void> {
    await this.session.ensure();
    const db = this.session.db;
    const id = this.genId(PLATFORM_ENTRIES);
    const entry: HoursEntry = {
      id,
      tenantId,
      type,
      hours,
      description,
      createdAt: new Date(),
      createdBy,
      ...(relatedTicketId ? { relatedTicketId } : {}),
      ...(relatedOrderId ? { relatedOrderId } : {}),
    };
    const batch = writeBatch(db);

    const entryRef = doc(collection(db, PLATFORM_ENTRIES), id);
    batch.set(entryRef, entry);

    const delta = type === 'credit' ? hours : -hours;
    const tenantRef = doc(db, 'tenants', tenantId);
    batch.update(tenantRef, { hoursBalance: increment(delta) });

    await batch.commit();
  }

  // ---------------------------------------------------------------------------
  // Support Tickets
  // ---------------------------------------------------------------------------

  private ticketsPath(tenantId: string): string {
    return `tenants/${tenantId}/support_tickets`;
  }

  /** Tickets for a specific tenant */
  listTicketsByTenant(tenantId: string): Observable<SupportTicket[]> {
    return this.list$<SupportTicket>(
      this.ticketsPath(tenantId),
      orderBy('updatedAt', 'desc'),
    );
  }

  /** All tickets across all tenants using collectionGroup */
  listAllTickets(): Observable<SupportTicket[]> {
    return this.group$<SupportTicket>(
      'support_tickets',
      orderBy('updatedAt', 'desc'),
    );
  }

  getTicket(tenantId: string, ticketId: string): Observable<SupportTicket> {
    return this.doc$<SupportTicket>(this.ticketsPath(tenantId), ticketId);
  }

  createTicket(
    tenantId: string,
    title: string,
    description: string,
    priority: TicketPriority,
    createdBy: string,
  ): Promise<string> {
    const ticket: Omit<SupportTicket, 'createdAt' | 'updatedAt'> = {
      id: '',
      tenantId,
      title,
      description,
      status: 'open',
      priority,
      hoursConsumed: 0,
      createdBy,
      messages: [],
    };
    return this.createDoc<SupportTicket>(
      this.ticketsPath(tenantId),
      ticket as Partial<SupportTicket>,
    );
  }

  updateTicketStatus(
    tenantId: string,
    ticketId: string,
    status: TicketStatus,
  ): Promise<void> {
    return this.updateDocById<SupportTicket>(this.ticketsPath(tenantId), {
      id: ticketId,
      status,
    });
  }

  async addMessage(
    tenantId: string,
    ticketId: string,
    content: string,
    authorUid: string,
    authorName: string,
    isSuperAdmin: boolean,
    hoursConsumed?: number,
  ): Promise<void> {
    const ticket = await firstValueFrom(this.getTicket(tenantId, ticketId));

    if (!ticket) throw new Error('Ticket not found');

    const message: TicketMessage = {
      id: crypto.randomUUID(),
      content,
      authorUid,
      authorName,
      isSuperAdmin,
      createdAt: new Date(),
      ...(hoursConsumed ? { hoursConsumed } : {}),
    };

    const updatedMessages = [...(ticket.messages ?? []), message];
    const updatedHours = (ticket.hoursConsumed ?? 0) + (hoursConsumed ?? 0);

    await this.updateDocById<SupportTicket>(this.ticketsPath(tenantId), {
      id: ticketId,
      messages: updatedMessages,
      hoursConsumed: updatedHours,
      status: ticket.status === 'open' ? 'in-progress' : ticket.status,
    });

    // If hours consumed, register a debit entry
    if (hoursConsumed && hoursConsumed > 0) {
      await this.addManualEntry(
        tenantId,
        'debit',
        hoursConsumed,
        `Ticket #${ticketId.slice(0, 8)}: ${content.slice(0, 60)}`,
        authorUid,
        ticketId,
      );
    }
  }

  async sendEstimation(
    tenantId: string,
    ticketId: string,
    hours: number,
    description: string,
  ): Promise<void> {
    const estimation: TicketEstimation = {
      hours,
      description,
      status: 'pending',
      createdAt: new Date(),
    };
    await this.updateDocById<SupportTicket>(this.ticketsPath(tenantId), {
      id: ticketId,
      pendingEstimation: estimation,
      status: 'in-progress',
    });
  }

  async respondToEstimation(
    tenantId: string,
    ticketId: string,
    accept: boolean,
  ): Promise<void> {
    await this.callable<
      { ticketId: string; accept: boolean },
      { success: boolean }
    >('respondToEstimation', { ticketId, accept });
  }
}
